<template>
  <div class="share-page">
    <header class="share-header">
      <div class="share-header__inner">
        <router-link to="/" class="share-header__logo">
          <span class="share-header__logo-go">Go</span> Trust
        </router-link>
        <button type="button" class="share-header__dl" @click="goDownload">
          下载 App
        </button>
      </div>
    </header>

    <main class="share-main">
      <div v-if="loading" class="share-state">加载中…</div>
      <div v-else-if="error" class="share-state share-state--error">
        <p>{{ error }}</p>
        <button type="button" class="share-retry" @click="load">重试</button>
      </div>
      <template v-else-if="course">
        <section class="hero-card">
          <div class="hero-card__body">
            <h1 class="hero-card__title">{{ course.name || '课程详情' }}</h1>
            <p v-if="priceText" class="hero-card__price">￥{{ priceText }}</p>
            <div v-if="tags.length" class="tag-row">
              <span v-for="(tag, i) in tags" :key="i" class="tag">{{ tag }}</span>
            </div>
          </div>
        </section>

        <section v-if="teacher" class="block-card" @click="goTeacher">
          <div class="teacher-row">
            <div class="teacher-row__avatar">
              <img
                v-if="teacher.avatar"
                :src="teacher.avatar"
                :alt="teacherName"
              />
              <span v-else>{{ teacherName.slice(0, 1) }}</span>
            </div>
            <div class="teacher-row__meta">
              <p class="teacher-row__name">{{ teacherName }}</p>
              <p v-if="teacher.skill" class="teacher-row__skill">
                {{ teacherSkillPreview }}
              </p>
            </div>
            <span class="teacher-row__arrow">›</span>
          </div>
        </section>

        <section v-if="course.description" class="block-card">
          <h2 class="block-card__title">课程介绍</h2>
          <p class="block-card__text">{{ course.description }}</p>
        </section>

        <section v-if="lessons.length" class="block-card">
          <h2 class="block-card__title">课时安排（{{ lessons.length }}）</h2>
          <ul class="lesson-list">
            <li v-for="(lesson, index) in lessons" :key="lesson.id || index">
              <span class="lesson-list__idx">{{ index + 1 }}</span>
              <span class="lesson-list__title">{{ lesson.title || '课时' }}</span>
              <span v-if="lesson.duration" class="lesson-list__dur">
                {{ lesson.duration }}分钟
              </span>
            </li>
          </ul>
        </section>
      </template>
    </main>

    <ShareBottomBar @open-app="goDownload" />
  </div>
</template>

<script>
import ShareBottomBar from '../components/ShareBottomBar.vue'
import { fetchCourseInfo } from '../api/share'
import { setShareMeta } from '../utils/shareMeta'

export default {
  name: 'CourseSharePage',
  components: { ShareBottomBar },
  data() {
    return {
      loading: true,
      error: '',
      course: null,
    }
  },
  computed: {
    courseId() {
      return this.$route.params.id
    },
    teacher() {
      return this.course?.teacher || null
    },
    teacherName() {
      const t = this.teacher
      if (!t) return '老师'
      return (
        t.nickname ||
        t.username ||
        t.name ||
        '老师'
      )
    },
    teacherSkillPreview() {
      const skill = (this.teacher?.skill || '').trim()
      if (!skill) return ''
      return skill
        .split(/[,，/、|]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3)
        .join(' · ')
    },
    priceText() {
      const raw = this.course?.price_label || this.course?.price
      if (raw == null || raw === '') return ''
      return String(raw).replace(/^￥|^¥/, '')
    },
    tags() {
      const raw = this.course?.tag
      if (!raw) return []
      if (Array.isArray(raw)) return raw.filter(Boolean)
      return String(raw)
        .split(/[,，/、|]/)
        .map((s) => s.trim())
        .filter(Boolean)
    },
    lessons() {
      const list = this.course?.lesson
      return Array.isArray(list) ? list : []
    },
  },
  watch: {
    courseId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    async load() {
      const id = this.courseId
      if (!id) {
        this.loading = false
        this.error = '缺少课程 ID'
        this.course = null
        return
      }
      this.loading = true
      this.error = ''
      try {
        this.course = await fetchCourseInfo(id)
        const name = (this.course?.name || '').trim()
        const desc = (this.course?.description || '').trim()
        setShareMeta({
          title: name ? `课程${name} - Go Trust` : '课程详情 - Go Trust',
          description: desc
            ? desc.slice(0, 80)
            : '名师课程、留学资讯一站式服务，打开查看详情并下载 App',
          url: window.location.href,
        })
      } catch (e) {
        this.course = null
        this.error = e?.message || '加载失败'
        setShareMeta({ title: '课程详情 - Go Trust' })
      } finally {
        this.loading = false
      }
    },
    goDownload() {
      this.$router.push('/download')
    },
    goTeacher() {
      const id = this.course?.teacher_id || this.teacher?.id
      if (!id) {
        this.goDownload()
        return
      }
      this.$router.push(`/teacher/${id}`)
    },
  },
}
</script>

<style scoped>
.share-page {
  min-height: 100vh;
  background: var(--color-bg);
  padding-bottom: 88px;
}

.share-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--color-primary);
  height: var(--header-height);
}

.share-header__inner {
  max-width: 720px;
  margin: 0 auto;
  height: 100%;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.share-header__logo {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.share-header__logo-go {
  color: var(--color-accent);
}

.share-header__dl {
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.share-main {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}

.share-state {
  padding: 64px 16px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.share-state--error {
  color: #c0392b;
}

.share-retry {
  margin-top: 12px;
  height: 36px;
  padding: 0 16px;
  border-radius: 18px;
  background: var(--color-primary);
  color: #fff;
  font-size: 13px;
}

.hero-card,
.block-card {
  background: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  margin-bottom: 12px;
}

.hero-card__body {
  padding: 16px;
}

.hero-card__title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--color-text);
}

.hero-card__price {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-accent);
}

.tag-row {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--color-tag-bg);
  color: var(--color-text-secondary);
  font-size: 12px;
}

.block-card {
  padding: 16px;
}

.block-card__title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.block-card__text {
  font-size: 14px;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  line-height: 1.7;
}

.teacher-row {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.teacher-row__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-tag-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-weight: 700;
  flex-shrink: 0;
}

.teacher-row__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.teacher-row__meta {
  flex: 1;
  min-width: 0;
}

.teacher-row__name {
  font-size: 15px;
  font-weight: 600;
}

.teacher-row__skill {
  margin-top: 2px;
  font-size: 12px;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.teacher-row__arrow {
  color: var(--color-text-light);
  font-size: 22px;
}

.lesson-list {
  list-style: none;
}

.lesson-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid var(--color-border);
  font-size: 14px;
}

.lesson-list li:first-child {
  border-top: none;
  padding-top: 0;
}

.lesson-list__idx {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--color-tag-bg);
  color: var(--color-text-secondary);
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.lesson-list__title {
  flex: 1;
  min-width: 0;
}

.lesson-list__dur {
  color: var(--color-text-light);
  font-size: 12px;
  flex-shrink: 0;
}
</style>
